import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import apiClient from '../api/apiClient';
import { useAuth } from './AuthContext';

const DataContext = createContext();

export const useData = () => {
  const ctx = useContext(DataContext);
  if (!ctx) {
    throw new Error('useData must be used within a DataProvider');
  }
  return ctx;
};

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [servers, setServers] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [serverCount, setServerCount] = useState({ totalCount: 0, upCount: 0, downCount: 0 });
  const [isPrefetching, setIsPrefetching] = useState(false);

  const organization = user?.organizationId || user?.organization || 'default';
  const isAdmin = user?.role === 'admin';

  const reset = useCallback(() => {
    setServers([]);
    setEnvironments([]);
    setRequests([]);
    setUsers([]);
    setServerCount({ totalCount: 0, upCount: 0, downCount: 0 });
    setIsPrefetching(false);
  }, []);

  const refreshServers = useCallback(async () => {
    if (!user) {
      reset();
      return [];
    }
    const response = await apiClient.get('/api/servers', { params: { organization } });
    setServers(response.data || []);
    return response.data || [];
  }, [organization, reset, user]);

  const refreshEnvironments = useCallback(async () => {
    if (!user) {
      reset();
      return [];
    }
    const response = await apiClient.get('/api/env', { params: { organization } });
    setEnvironments(Array.isArray(response.data) ? response.data : []);
    return Array.isArray(response.data) ? response.data : [];
  }, [organization, reset, user]);

  const refreshRequests = useCallback(async () => {
    if (!user || !isAdmin) {
      setRequests([]);
      return [];
    }
    const response = await apiClient.get('/api/requests', { params: { organization } });
    setRequests(response.data || []);
    return response.data || [];
  }, [isAdmin, organization, user]);

  const refreshUsers = useCallback(async () => {
    if (!user || !isAdmin) {
      setUsers([]);
      return [];
    }
    const response = await apiClient.get('/api/users/all', { params: { organization } });
    setUsers(response.data?.users || []);
    return response.data?.users || [];
  }, [isAdmin, organization, user]);

  const refreshServerCount = useCallback(async () => {
    if (!user) {
      reset();
      return { totalCount: 0, upCount: 0, downCount: 0 };
    }
    const response = await apiClient.get('/api/servers/count/status-count', { params: { organization } });
    const counts = {
      totalCount: response.data?.totalCount || 0,
      upCount: response.data?.upCount || 0,
      downCount: response.data?.downCount || 0
    };
    setServerCount(counts);
    return counts;
  }, [organization, reset, user]);

  const refreshAll = useCallback(async () => {
    if (!user) {
      reset();
      return;
    }
    setIsPrefetching(true);
    try {
      await Promise.all([
        refreshServers(),
        refreshEnvironments(),
        refreshServerCount(),
        refreshRequests(),
        refreshUsers()
      ]);
    } catch (err) {
      console.error('Error prefetching data:', err);
    } finally {
      setIsPrefetching(false);
    }
  }, [refreshEnvironments, refreshRequests, refreshServerCount, refreshServers, refreshUsers, reset, user]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const value = useMemo(() => ({
    servers,
    environments,
    requests,
    users,
    serverCount,
    isPrefetching,
    refreshServers,
    refreshEnvironments,
    refreshRequests,
    refreshUsers,
    refreshServerCount,
    refreshAll,
  }), [environments, isPrefetching, refreshAll, refreshEnvironments, refreshRequests, refreshServerCount, refreshServers, refreshUsers, requests, serverCount, servers, users]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
